/*
*   @site           Preloaded: Site
*   @function       Link Controller
*   @author         Last17
*   @copyright      Copyright 2025, Preloaded.com
*   @version        0.01
*
*********************************************************************************************/


//-----------------------------------------------
// Imports
//-----------------------------------------------
import inView from 'in-view';
import NavigationController from './NavigationController';

//-----------------------------------------------
// Class Definition
//-----------------------------------------------
class _LinkController {

  //-----------------------------------------------
  // Constructor
  //-----------------------------------------------
  constructor() {

    //Theming
    this.theme = {
      current: '',
      default: 'white'
    };

    //Pos
    this.indicatorPosition = {
      scroll: { x: 0, y: 0, offsetX: 0, offsetY:0 },
      current: { x: 0, y: 0 },
      target: { x: 0, y: 0 },
      tracking: false,
      lag: 5
    };

    //Flags
    this.allowRollovers = true;

    //Indicator
    this.indicator = document.querySelector('.link-rollover:not(.demo)');
    if (this.indicator) {
      this.indicatorInner = this.indicator.querySelector('.link-rollover__content');
      this.theme.default = this.indicator.getAttribute('data-theme') || 'white';
      this.setTheme(this.theme.default, false);
    }

    //Mouse Move
    if (this.indicator) {
      this.mouseTracker = window.addEventListener('mousemove', event => {
        if (this.indicatorPosition.tracking) {
          //Reset the scroll position
          this.indicatorPosition.scroll.x = window.scrollX;
          this.indicatorPosition.scroll.y = window.scrollY;
          this.indicatorPosition.scroll.offsetX = 0;
          this.indicatorPosition.scroll.offsetY = 0;
          
          //Create the target position
          this.indicatorPosition.target.x = event.pageX;
          this.indicatorPosition.target.y = event.pageY;
        }
      });

      //On window scroll adjust the target position based 
      window.addEventListener('scroll', (event) => {
        if (this.indicatorPosition.tracking) {
          this.indicatorPosition.scroll.offsetX = window.scrollX - this.indicatorPosition.scroll.x;
          this.indicatorPosition.scroll.offsetY = window.scrollY - this.indicatorPosition.scroll.y;
        }
      });
    }
  }


  //-----------------------------------------------
  // Init
  //-----------------------------------------------
  init() {
    //Look for links
    this.rolloverInit(document.querySelectorAll('a'));

    //Bind site links (contact us etc.)
    this.bindSiteLinks();
  }


  //-----------------------------------------------
  // Bind site list
  //-----------------------------------------------
  bindSiteLinks() {
    let list = document.querySelectorAll('a[data-link-id]');
    for(let i = 0; i < list.length; i++) {
      list[i].addEventListener('click', function(id, event) {
        //Click
        this.linkProcess(id);

        //Stop normal link actions
        event.preventDefault();
        event.stopPropagation();
      }.bind(this, list[i].getAttribute('data-link-id')));
    }
  }

  //-----------------------------------------------
  // Open the menu
  //-----------------------------------------------
  linkProcess(id) {
    if(id === 'contact') {
      (!NavigationController.config.isOpen) ? NavigationController.open(id) : NavigationController.pageSet(id, true, true);
    }
  }


  //-----------------------------------------------
  // Rollover Init
  //-----------------------------------------------
  rolloverInit(elements) {
    if (this.indicator) {
      for (let i = 0; i < elements.length; i++) {
        //Links with a theme set AND/OR an image as an immediate descendent
        if (elements[i].getAttribute('data-rollover-theme') || (elements[i].firstElementChild && elements[i].firstElementChild.classList.contains('image'))) {
          //Throttle rollovers when a touchstart is detected (this event comes first), then restore after any click, this should allow dual touch and mouse devices to work
          elements[i].addEventListener('touchstart', event => { this.allowRollovers = false; });
          elements[i].addEventListener('click', event => { this.allowRollovers = true; });

          //Mouse over and out
          elements[i].addEventListener('mouseover', event => {
            if(this.allowRollovers) {
              this.itemOver(elements[i], event);
            }
          });
          elements[i].addEventListener('mouseout', event => {
            if(this.allowRollovers) {
              this.itemOut(elements[i], event);
            }
          });
        }
      }
    }
  }


  //-----------------------------------------------
  // Roll Over
  //-----------------------------------------------
  itemOver(element, event) {
    //Remove follower
    if (this.stopFollowTimer) {
      clearTimeout(this.stopFollowTimer);
      this.stopFollowTimer = null;
    }

    //Add a rollover class
    this.indicator.classList.add('active');

    //Classes
    this.setTheme(element.getAttribute('data-rollover-theme') || this.theme.default, element.getAttribute('data-rollover-theme-alt') || false);

    //Store the current scroll positions (this allows us to offset the target when scroll events are called)
    this.indicatorPosition.scroll.x = window.scrollX;
    this.indicatorPosition.scroll.y = window.scrollY;
    this.indicatorPosition.scroll.offsetX = 0;
    this.indicatorPosition.scroll.offsetY = 0;

    //Target position set
    this.indicatorPosition.target.x = event.pageX;
    this.indicatorPosition.target.y = event.pageY;

    //Snap position
    if (Math.abs(event.pageX - this.indicatorPosition.current.x) > 250) {
      this.indicator.style['left'] = event.pageX + 'px';
      this.indicatorPosition.current.x = event.pageX;
    }
    if (Math.abs(event.pageY - this.indicatorPosition.current.y) > 250) {
      this.indicator.style['top'] = event.pageY + 'px';
      this.indicatorPosition.current.y = event.pageY;
    }

    //Mover
    if (this.stepper) {
      cancelAnimationFrame(this.stepper);
      this.stepper = null;
    }
    this.stepper = requestAnimationFrame(() => { this.moveIndicator(); });

    //Tracking
    this.indicatorPosition.tracking = true;
  }


  //-----------------------------------------------
  // Image Out
  //-----------------------------------------------
  itemOut(element, event) {
    //Stop
    this.indicator.classList.remove('active');

    if (this.stopFollowTimer) {
      clearTimeout(this.stopFollowTimer);
      this.stopFollowTimer = null;
    }

    this.stopFollowTimer = setTimeout(() => {
      this.indicatorPosition.tracking = false;
      this.stopFollowTimer = null;
    }, 1000);

  }


  //-----------------------------------------------
  // Set the theme
  //-----------------------------------------------
  setTheme(id, alt) {
    if (id !== this.theme.current) {
      //Old off
      if (this.theme.current !== '') {
        this.indicator.classList.remove('theme-' + this.theme.current);
      }

      //Set new
      this.indicator.classList.add('theme-' + id);
      this.theme.current = id;
    }

    //Alt version for targetted pages
    if(alt && !this.indicatorInner.classList.contains('alt')) {
      this.indicatorInner.classList.add('alt');
    } else if(!alt && this.indicatorInner.classList.contains('alt')) {
      this.indicatorInner.classList.remove('alt');
    }
  }


  //-----------------------------------------------
  // Move indicator
  //-----------------------------------------------
  moveIndicator() {
    //Calculate the difference
    let diffX = (this.indicatorPosition.target.x + this.indicatorPosition.scroll.offsetX) - this.indicatorPosition.current.x;
    let incX = diffX / this.indicatorPosition.lag;
    if (Math.abs(incX) < 0.5) { incX = diffX; }
    let diffY = (this.indicatorPosition.target.y + this.indicatorPosition.scroll.offsetY) - this.indicatorPosition.current.y;
    let incY = diffY / this.indicatorPosition.lag;
    if (Math.abs(incY) < 0.5) { incY = diffY; }

    if (diffX !== 0) {
      this.indicator.style['left'] = (this.indicatorPosition.current.x + incX) + 'px';
      this.indicatorPosition.current.x += incX;
    }
    if (diffY !== 0) {
      this.indicator.style['top'] = (this.indicatorPosition.current.y + incY) + 'px';
      this.indicatorPosition.current.y += incY;
    }

    //Request again
    if (this.indicatorPosition.tracking) {
      this.stepper = requestAnimationFrame(() => { this.moveIndicator(); });
    }
  }

}


//--------------------------------------------------------------------------
//  Export
//--------------------------------------------------------------------------
const LinkController = new _LinkController();
export default LinkController;